import { useState } from "react";
import axios from "axios";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Download, FileText, CheckCircle2, AlertTriangle, Info, Copy } from "lucide-react";
import { toast } from "sonner";
import { formatByType } from "../../lib/format";
import ChartBlock from "../charts/ChartBlock";
import { downloadCSV, printResultPDF } from "../../lib/export";
import { analytics } from "../../lib/analytics/ga4";
import LeadGateModal from "./LeadGateModal";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyles = {
  success: { cls: "border-brand/20 bg-brand/5 text-brand", Icon: CheckCircle2 },
  warning: { cls: "border-amber-200 bg-amber-50 text-amber-900", Icon: AlertTriangle },
  info: { cls: "border-sky-200 bg-sky-50 text-sky-900", Icon: Info },
};

export default function ResultPanel({ result, calc, values }) {
  const [showSchedule, setShowSchedule] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [pendingKind, setPendingKind] = useState(null);
  const { user } = useAuth();
  const isPlus = !!user?.plus;
  if (!result) return null;
  const { primary, secondary = [], chart, chart2, schedule, status, warning, note, downloadRows } = result;

  const runCSV = () => {
    const rows = downloadRows ? downloadRows() : [
      ["Result", "Value"],
      [primary.label, formatByType(primary.value, primary.type)],
      ...secondary.map((s) => [s.label, s.type === "raw" ? s.value : formatByType(s.value, s.type)]),
    ];
    downloadCSV(`${calc.slug}-result`, rows);
    analytics.resultDownload(calc.slug, "csv");
    toast.success("CSV downloaded");
  };

  const runPDF = () => {
    printResultPDF({
      title: calc.name,
      subtitle: `MyFinancesGuru Tools · ${new Date().toLocaleDateString("en-CA")}`,
      sections: [
        { heading: "Result", rows: [[primary.label, formatByType(primary.value, primary.type)], ...secondary.map((s) => [s.label, s.type === "raw" ? String(s.value) : formatByType(s.value, s.type)])] },
      ],
    });
    analytics.resultDownload(calc.slug, "pdf");
  };

  const runExport = (kind) => (kind === "csv" ? runCSV() : runPDF());

  // Lead-capture gate: first download asks for name+email; after that it's silent.
  // The download itself is never blocked if the lead write fails.
  const guard = (kind) => {
    // Plus members: full CSV + PDF, no lead prompt.
    if (isPlus) { runExport(kind); return; }
    // Free members: PDF (with charts) is a Plus perk.
    if (kind === "pdf") {
      toast.info("Branded PDF export is a Guru Plus feature.");
      return;
    }
    const email = typeof window !== "undefined" && localStorage.getItem("mfg_lead_email");
    if (email) {
      axios.post(`${API}/lead-capture`, { email, name: localStorage.getItem("mfg_lead_name") || "", calculator: calc.slug }).catch(() => {});
      runExport(kind);
    } else {
      setPendingKind(kind);
      setGateOpen(true);
    }
  };

  const doCSV = () => guard("csv");
  const doPDF = () => guard("pdf");

  const copyResult = () => {
    navigator.clipboard?.writeText(`${primary.label}: ${formatByType(primary.value, primary.type)}`);
    toast.success("Result copied");
  };

  const StatusIcon = status ? statusStyles[status.type]?.Icon : null;

  return (
    <div className="space-y-6" data-testid="result-panel">
      {/* Primary result */}
      <div className="relative overflow-hidden rounded-2xl border border-brand/15 bg-gradient-to-br from-brand to-[#0A3B2C] p-7 text-white animate-fade-up">
        <div className="grid-texture pointer-events-none absolute inset-0 opacity-30" />
        <div className="relative">
          <p className="text-sm font-medium text-white/70">{primary.label}</p>
          <div className="mt-1 flex items-end gap-3">
            <span data-testid="primary-result" className="font-heading text-4xl font-bold tracking-tight sm:text-5xl">
              {formatByType(primary.value, primary.type)}
            </span>
            <button onClick={copyResult} data-testid="copy-result" className="mb-2 rounded-md p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white" aria-label="Copy result">
              <Copy className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Secondary metrics */}
      {secondary.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {secondary.map((s, i) => (
            <div key={i} data-testid={`secondary-${i}`} className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs font-medium text-muted-foreground">{s.label}</p>
              <p className="mt-1 font-heading text-lg font-bold text-foreground">
                {s.type === "raw" ? s.value : formatByType(s.value, s.type)}
              </p>
              {s.hint && <p className="mt-0.5 text-xs text-muted-foreground">{s.hint}</p>}
            </div>
          ))}
        </div>
      )}

      {status && StatusIcon && (
        <div data-testid="result-status" className={`flex items-start gap-3 rounded-xl border p-4 text-sm ${statusStyles[status.type].cls}`}>
          <StatusIcon className="mt-0.5 h-5 w-5 shrink-0" /> <span>{status.text}</span>
        </div>
      )}
      {warning && (
        <div data-testid="result-warning" className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" /> <span>{warning}</span>
        </div>
      )}

      {/* Charts */}
      {(chart || chart2) && (
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className={chart2 ? "grid gap-8 md:grid-cols-2 [&>*]:min-w-0" : ""}>
            {chart && <ChartBlock chart={chart} />}
            {chart2 && <ChartBlock chart={chart2} />}
          </div>
        </div>
      )}

      {note && <p className="text-xs italic text-muted-foreground">{note}</p>}

      {/* Downloads */}
      <div className="flex flex-wrap gap-3">
        <button onClick={doCSV} data-testid="download-csv" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium transition-colors hover:border-brand hover:text-brand">
          <Download className="h-4 w-4" /> Download CSV
        </button>
        <button onClick={doPDF} data-testid="download-pdf" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium transition-colors hover:border-brand hover:text-brand">
          <FileText className="h-4 w-4" /> {isPlus ? "Save as PDF" : "PDF with charts"}
          {!isPlus && <span className="rounded-full bg-brand/10 px-1.5 py-0.5 text-[10px] font-bold text-brand">PLUS</span>}
        </button>
        {!isPlus && (
          <Link to="/guru-plus" data-testid="results-plus-link" className="inline-flex items-center gap-1 self-center text-xs font-semibold text-brand hover:text-action">
            Unlock full exports →
          </Link>
        )}
      </div>

      <LeadGateModal
        open={gateOpen}
        onOpenChange={setGateOpen}
        calculator={calc.slug}
        onUnlock={() => { if (pendingKind) runExport(pendingKind); }}
      />

      {/* Schedule */}
      {schedule && (
        <Accordion type="single" collapsible className="rounded-2xl border border-border bg-card px-5">
          <AccordionItem value="schedule" className="border-none">
            <AccordionTrigger data-testid="toggle-schedule" onClick={() => setShowSchedule(true)} className="font-heading text-sm font-bold">
              {schedule.title}
            </AccordionTrigger>
            <AccordionContent>
              <div className="max-h-96 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>{schedule.columns.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow>
                  </TableHeader>
                  <TableBody>
                    {schedule.rows.map((r, i) => (
                      <TableRow key={i}>{r.map((cell, j) => <TableCell key={j} className={j === 0 ? "font-medium" : ""}>{cell}</TableCell>)}</TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
}
